import {
    CustomAction,
    CustomActionsPosition,
    CustomActionTarget,
    EmbedContextOptions,
} from '@thoughtspot/blink-context';
import { create } from '@thoughtspot/logger';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { customActionService } from '/@services/answer/custom-action-service';
import {
    ActionTypes,
    DisplayMode,
    EmbedActionConfig,
} from '/@services/generated/graphql-types';
import {
    CODE_BASED_CUSTOM_ACTION_ERROR_MESSAGES,
    CodeBasedCustomActionErrorType,
    EventType,
    getCodeBasedCustomActionErrorMessage,
    getHiddenActions,
    getVisibleActions,
    shouldHideForEmbed,
    shouldShowAction,
    urlAuthHeadersForCutomActions,
} from '/@utils/embed.util';

enum AnswerPageContext {
    CONV_ASSIST_ANSWER = 'CONV_ASSIST_ANSWER',
}

// Constants for filter types to improve maintainability and reusability
export const FILTER_TYPES = {
    ORG_IDS: 'orgIds',
    GROUP_IDS: 'groupIds',
    LIVEBOARD_IDS: 'liveboardIds',
    MODEL_IDS: 'modelIds',
    ANSWER_IDS: 'answerIds',
    VIZ_IDS: 'vizIds',
} as const;

export type FilterType = typeof FILTER_TYPES[keyof typeof FILTER_TYPES];

const logger = create('custom-actions-util');

export interface FilterErrorConfig {
    emptyErrorType: CodeBasedCustomActionErrorType;
    noMatchErrorType: CodeBasedCustomActionErrorType;
}

/**
 * Check if a single action passes the filter for a given property
 */
export const isActionAllowed = (
    action: CustomAction,
    filterType: FilterType,
    allowedValues: string[],
    errorConfig: FilterErrorConfig,
    dispatchEmbedEvent: (eventType: string, payload: any) => void,
    errorParams: Record<string, string> = {},
): {
    isActionAllowed: boolean;
    error: string | null;
    isValueMatch?: boolean;
} => {
    let propertyValues: string[] | undefined;

    switch (filterType) {
        case FILTER_TYPES.LIVEBOARD_IDS:
        case FILTER_TYPES.ANSWER_IDS:
        case FILTER_TYPES.VIZ_IDS: {
            // Implies global custom action so return true.
            if (!Object.prototype.hasOwnProperty.call(action, 'metadataIds')) {
                return { isActionAllowed: true, error: null };
            }
            propertyValues = action.metadataIds?.[filterType];
            break;
        }
        case FILTER_TYPES.MODEL_IDS: {
            // Must have dataModelIds defined
            if (!Object.prototype.hasOwnProperty.call(action, 'dataModelIds')) {
                return { isActionAllowed: true, error: null };
            }
            propertyValues = action.dataModelIds?.[filterType];
            break;
        }

        case FILTER_TYPES.ORG_IDS:
        case FILTER_TYPES.GROUP_IDS: {
            // global in case groupIds / orgIds are not passed.
            if (!Object.prototype.hasOwnProperty.call(action, filterType)) {
                return { isActionAllowed: true, error: null };
            }
            propertyValues = action[filterType];
            break;
        }

        default: {
            const errorMessage = CODE_BASED_CUSTOM_ACTION_ERROR_MESSAGES.INVALID_FILTER_TYPE(
                action.name,
                action.id,
                filterType,
            );
            return { isActionAllowed: false, error: errorMessage };
        }
    }

    // If user let say passes metadataIds but doesn't pass the propertyValues like liveboardIds,
    // return false.
    if (!propertyValues || !Array.isArray(propertyValues)) {
        return {
            isActionAllowed: false,
            error: CODE_BASED_CUSTOM_ACTION_ERROR_MESSAGES.INVALID_FILTER_TYPE(
                action.name,
                action.id,
                filterType,
            ),
        };
    }

    // If property is empty array, return false
    if (propertyValues.length === 0) {
        const errorMessage = getCodeBasedCustomActionErrorMessage(
            errorConfig.emptyErrorType,
            action.name,
            action.id,
        );
        return { isActionAllowed: false, error: errorMessage };
    }

    // Check if any of the action's values match the allowed values
    const isMatch = propertyValues.some((value: string) =>
        allowedValues.includes(value),
    );

    if (!isMatch) {
        // Prepare error parameters with dynamic values based on filter type
        const dynamicErrorParams = {
            ...errorParams,
            // Add dynamic values based on the current filter type
            ...(filterType === FILTER_TYPES.ORG_IDS && {
                orgIds: propertyValues?.join(', ') || '',
            }),
            ...(filterType === FILTER_TYPES.GROUP_IDS && {
                groupIds: propertyValues?.join(', ') || '',
            }),
            ...(filterType === FILTER_TYPES.LIVEBOARD_IDS && {
                liveboardIds: propertyValues?.join(', ') || '',
            }),
            ...(filterType === FILTER_TYPES.MODEL_IDS && {
                modelIds: propertyValues?.join(', ') || '',
            }),
            ...(filterType === FILTER_TYPES.ANSWER_IDS && {
                answerIds: propertyValues?.join(', ') || '',
            }),
            ...(filterType === FILTER_TYPES.VIZ_IDS && {
                vizIds: propertyValues?.join(', ') || '',
            }),
        };

        const errorMessage = getCodeBasedCustomActionErrorMessage(
            errorConfig.noMatchErrorType,
            action.name,
            action.id,
            dynamicErrorParams,
        );
        return { isActionAllowed: false, error: errorMessage };
    }

    return { isActionAllowed: isMatch, error: null, isValueMatch: isMatch };
};

/**
 * Common filtering method for custom actions by different properties (orgIds, groupIds, liveboardIds, etc.)
 * This function can be reused across different components that need to filter custom actions
 */
export const filterActionsByProperty = (
    actions: CustomAction[],
    filterType: FilterType,
    allowedValues: string[],
    errorConfig: FilterErrorConfig,
    dispatchEmbedEvent: (eventType: string, payload: any) => void,
    errorParams: Record<string, string> = {},
): CustomAction[] => {
    const errors: string[] = [];

    const filteredActions = actions.filter(action => {
        const result = isActionAllowed(
            action,
            filterType,
            allowedValues,
            errorConfig,
            dispatchEmbedEvent,
            errorParams,
        );
        if (result.error) {
            errors.push(result.error);
        }
        return result.isActionAllowed;
    });

    // Dispatch all errors at once if there are any
    if (errors.length > 0) {
        dispatchEmbedEvent(EventType.Error, {
            errors,
        });
        logger.error(errors);
    }

    return filteredActions;
};

/**
 * Common filtering logic for answer context (both primary and menu positions)
 */
export const filterCodeBasedCustomActionsForAnswerContext = (
    embedConfig: EmbedContextOptions,
    answerId: string,
    worksheetId: string | undefined,
    columnsName: string[],
    position: CustomActionsPosition,
    legacyCustomActions: any = [],
): CustomAction[] => {
    if (!answerId || !columnsName.length || !worksheetId) {
        return [];
    }
    const { codeBasedCustomActions = [] } = embedConfig;

    // First filter by visibility and hidden actions
    const visibleActions = getVisibleActions(embedConfig);
    const effectiveHiddenActions = getHiddenActions(embedConfig);

    const filteredActions = codeBasedCustomActions.filter(
        (action: CustomAction) => {
            // Check position and target criteria
            if (
                action.position !== position ||
                action.target !== CustomActionTarget.ANSWER
            ) {
                return false;
            }

            // Check visibility filter
            const shouldShow = shouldShowAction(
                visibleActions,
                effectiveHiddenActions,
                action.id,
            );
            if (!shouldShow) {
                return false;
            }

            // Check hidden actions if provided
            if (effectiveHiddenActions?.has(action.id)) {
                return false;
            }

            return true;
        },
    );

    const errors: string[] = [];

    const finalFilteredActions = filteredActions.filter(action => {
        // Check if legacy custom actions exist at primary action position and throw error if they do
        if (
            legacyCustomActions &&
            legacyCustomActions.length > 0 &&
            position === CustomActionsPosition.PRIMARY
        ) {
            const errorMessage = CODE_BASED_CUSTOM_ACTION_ERROR_MESSAGES.LEGACY_CUSTOM_ACTIONS_CONFLICT();
            errors.push(errorMessage);
            return false;
        }
        const answerIdResult = isActionAllowed(
            action,
            FILTER_TYPES.ANSWER_IDS,
            [answerId],
            {
                emptyErrorType: CodeBasedCustomActionErrorType.AnswerIdsEmpty,
                noMatchErrorType:
                    CodeBasedCustomActionErrorType.InvalidAnswerId,
            },
            embedConfig.dispatchEmbedEvent,
            { currentAnswerId: answerId },
        );

        const modelIdResult = isActionAllowed(
            action,
            FILTER_TYPES.MODEL_IDS,
            [worksheetId],
            {
                emptyErrorType: CodeBasedCustomActionErrorType.ModelIdsEmpty,
                noMatchErrorType: CodeBasedCustomActionErrorType.InvalidModelId,
            },
            embedConfig.dispatchEmbedEvent,
            { currentModelId: worksheetId },
        );

        const columnNameMatch = action?.dataModelIds?.modelColumnNames?.some(
            (columnNameWithWorksheet: string) => {
                const [
                    actionWorksheetId,
                    columnName,
                ] = columnNameWithWorksheet.split('::');
                return (
                    actionWorksheetId === worksheetId &&
                    columnsName.includes(columnName)
                );
            },
        );

        // Firstly think about global custom action for that, dataModelIds, metadataIds should not be present
        // in that case, modelIdsResult and answerId result both should be true.
        if (modelIdResult.isActionAllowed && answerIdResult.isActionAllowed) {
            return true;
        }

        // Now we will return true if answerIdResult.isValueMatch is true || modelIdResult.isValueMatch is true
        // or columnNameMatch is true.
        if (
            answerIdResult.isValueMatch ||
            modelIdResult.isValueMatch ||
            columnNameMatch
        ) {
            return true;
        }

        // If we reach here, IDs were provided but none matched - collect errors for this action
        // Use the stored results from isActionAllowed calls
        if (answerIdResult && answerIdResult.error) {
            errors.push(answerIdResult.error);
        }

        if (modelIdResult && modelIdResult.error) {
            errors.push(modelIdResult.error);
        }

        const columnNamesData = action?.dataModelIds?.modelColumnNames;
        if (columnNamesData && Array.isArray(columnNamesData)) {
            let error = '';
            if (columnNamesData.length)
                error = getCodeBasedCustomActionErrorMessage(
                    CodeBasedCustomActionErrorType.InvalidModelColumnNames,
                    action.name,
                    action.id,
                    {
                        currentWorksheetId: worksheetId || '',
                        availableColumns: columnsName?.join(', '),
                        modelColumnNames:
                            action?.dataModelIds?.modelColumnNames?.join(
                                ', ',
                            ) || '',
                    },
                );
            else
                error = getCodeBasedCustomActionErrorMessage(
                    CodeBasedCustomActionErrorType.ModelColumnNamesEmpty,
                    action.name,
                    action.id,
                );
            errors.push(error);
        }

        return false;
    });

    // If more than one primary action found, keep only the first and report an error
    let actionsToReturn = finalFilteredActions;
    if (
        position === CustomActionsPosition.PRIMARY &&
        finalFilteredActions.length > 1
    ) {
        const first = finalFilteredActions[0];
        const answerMultiPrimary = (CODE_BASED_CUSTOM_ACTION_ERROR_MESSAGES as any)
            .MULTIPLE_PRIMARY_ACTIONS_FOUND_ANSWER;
        errors.push(
            answerMultiPrimary
                ? answerMultiPrimary(first.name, first.id)
                : `Multiple primary code-based custom actions found for Answer. Showing only the first: "${first.name}" (ID: ${first.id}).`,
        );
        actionsToReturn = [first];
    }

    // Dispatch all collected errors at once
    if (errors.length > 0) {
        embedConfig.dispatchEmbedEvent(EventType.Error, {
            errors,
        });
        logger.error(errors);
    }

    return actionsToReturn;
};

/**
 * Common function to filter code-based custom actions for pinboard
 * Handles both primary and menu positions and returns two separate arrays
 */
export const filterCodeBasedCustomActionsForPinboard = (
    embedConfig: EmbedContextOptions,
    pinboardId: string,
): {
    primaryLiveboardLevelCustomActions: CustomAction[];
    menuLiveboardLevelCustomActions: CustomAction[];
} => {
    const { codeBasedCustomActions = [] } = embedConfig;
    const errors: string[] = [];
    const primaryActions: CustomAction[] = [];
    const menuActions: CustomAction[] = [];

    codeBasedCustomActions.forEach(customAction => {
        // Check if action is for LIVEBOARD target and not hidden
        if (
            customAction.target !== CustomActionTarget.LIVEBOARD ||
            (customAction.position !== CustomActionsPosition.PRIMARY &&
                customAction.position !== CustomActionsPosition.MENU) ||
            shouldHideForEmbed(embedConfig, customAction.id)
        ) {
            return;
        }

        // Check liveboard ID restrictions
        const result = isActionAllowed(
            customAction,
            FILTER_TYPES.LIVEBOARD_IDS,
            [pinboardId],
            {
                emptyErrorType:
                    CodeBasedCustomActionErrorType.LiveboardIdsEmpty,
                noMatchErrorType:
                    CodeBasedCustomActionErrorType.LiveboardIdNotMatched,
            },
            embedConfig.dispatchEmbedEvent,
            {
                currentPinboardId: pinboardId,
            },
        );

        if (result.error) {
            errors.push(result.error);
        }

        if (result.isActionAllowed) {
            if (customAction.position === CustomActionsPosition.PRIMARY) {
                primaryActions.push(customAction);
            } else {
                menuActions.push(customAction);
            }
        }
    });

    // If more than one primary action found, keep only the first and report an error
    let primaryActionsToReturn = primaryActions;
    if (primaryActions.length > 1) {
        const first = primaryActions[0];
        const liveboardMultiPrimary = (CODE_BASED_CUSTOM_ACTION_ERROR_MESSAGES as any)
            .MULTIPLE_PRIMARY_ACTIONS_FOUND_LIVEBOARD;
        errors.push(
            liveboardMultiPrimary
                ? liveboardMultiPrimary(first.name, first.id)
                : `Multiple primary code-based custom actions found for Liveboard. Showing only the first: "${first.name}" (ID: ${first.id}).`,
        );
        primaryActionsToReturn = [first];
    }

    // Dispatch all collected errors at once
    if (errors.length > 0) {
        embedConfig.dispatchEmbedEvent(EventType.Error, {
            errors,
        });
        logger.error(errors);
    }

    return {
        primaryLiveboardLevelCustomActions: primaryActionsToReturn,
        menuLiveboardLevelCustomActions: menuActions,
    };
};

/**
 * Generic function to filter code-based custom actions for spotter-related components
 * Combines position/target filtering with dataModelIds filtering in a single pass
 */
export const filterCodeBasedCustomActionsForSpotter = (
    embedConfig: EmbedContextOptions,
    worksheetId: string | undefined,
    options: {
        position: CustomActionsPosition;
        target: CustomActionTarget;
        pageContext?: string;
        hiddenActions?: Set<string>;
        useVisibilityFilter?: boolean;
    },
): CustomAction[] => {
    const { codeBasedCustomActions = [] } = embedConfig;
    const {
        position,
        target,
        pageContext,
        hiddenActions,
        useVisibilityFilter = false,
    } = options;

    let visibleActions: Set<string> | undefined;
    let effectiveHiddenActions: Set<string> | undefined;

    if (useVisibilityFilter) {
        visibleActions = getVisibleActions(embedConfig);
        effectiveHiddenActions = getHiddenActions(embedConfig);
    } else if (hiddenActions) {
        effectiveHiddenActions = hiddenActions;
    }

    const errors: string[] = [];

    const filteredActions = codeBasedCustomActions.filter(
        (action: CustomAction) => {
            // Check position and target criteria
            if (action.position !== position || action.target !== target) {
                return false;
            }

            // Check page context if provided
            if (
                pageContext &&
                pageContext !== AnswerPageContext.CONV_ASSIST_ANSWER
            ) {
                return false;
            }

            // Check visibility filter if enabled
            if (useVisibilityFilter && visibleActions) {
                const shouldShow = shouldShowAction(
                    visibleActions,
                    effectiveHiddenActions || new Set(),
                    action.id,
                );
                if (!shouldShow) {
                    return false;
                }
            }

            // Check hidden actions if provided
            if (effectiveHiddenActions?.has(action.id)) {
                return false;
            }

            // Use utility function to check dataModelIds restrictions
            const result = isActionAllowed(
                action,
                FILTER_TYPES.MODEL_IDS,
                worksheetId ? [worksheetId] : [],
                {
                    emptyErrorType:
                        CodeBasedCustomActionErrorType.ModelIdsEmpty,
                    noMatchErrorType:
                        CodeBasedCustomActionErrorType.InvalidModelId,
                },
                embedConfig.dispatchEmbedEvent,
                {
                    currentModelId: worksheetId || '',
                },
            );

            if (result.error) {
                errors.push(result.error);
            }

            return result.isActionAllowed;
        },
    );

    // Dispatch all collected errors at once
    if (errors.length > 0) {
        embedConfig.dispatchEmbedEvent(EventType.Error, {
            errors,
        });
        logger.error(errors);
    }

    return filteredActions;
};

export const getEventParamsForCBCA = (action: CustomAction) => {
    return {
        actionName: action.name,
        location: (action as CustomAction).position,
        isModelBased:
            (action as CustomAction)?.dataModelIds?.modelIds?.length > 0,
        isColumnsBased:
            (action as CustomAction)?.dataModelIds?.modelColumnNames?.length >
            0,
        isAnswerBased:
            (action as CustomAction)?.metadataIds?.answerIds?.length > 0,
        isLiveboardBased:
            (action as CustomAction)?.metadataIds?.liveboardIds?.length > 0,
        isVizBased: (action as CustomAction)?.metadataIds?.vizIds?.length > 0,
        isOrgBased: (action as CustomAction)?.orgIds?.length > 0,
        isGroupBased: (action as CustomAction)?.groupIds?.length > 0,
    };
};

/**
 * Shared utility function to handle custom action execution
 * Can be used by both answer-actions and conv-assist components
 */
export const executeCustomAction = async (
    action: EmbedActionConfig | CustomAction,
    options: {
        isCodeBased?: boolean;
        embedConfig: EmbedContextOptions;
        metricsService: any;
        getAnswerMixpanelProperties: () => any;
        cachedAnswerFragment: any;
        embedAnswerData: any;
        dispatchEmbedEvent: (eventType: string, payload: any) => Promise<any>;
        showToastAlert: (message: string) => void;
        showBannerAlert: (message: string, type?: any) => void;
        acSession: any;
        getBachSession: () => any;
        displayMode: DisplayMode;
        convMetadata?: any;
    },
) => {
    const {
        isCodeBased = false,
        metricsService,
        getAnswerMixpanelProperties,
        cachedAnswerFragment,
        embedAnswerData,
        dispatchEmbedEvent,
        showToastAlert,
        showBannerAlert,
        acSession,
        getBachSession,
        displayMode,
        convMetadata,
    } = options;

    const answerCommonProperties = getAnswerMixpanelProperties();
    const eventParams = {
        actionName: action.name,
        location: isCodeBased
            ? (action as CustomAction).position
            : (action as EmbedActionConfig).context,
        currentPage: isCodeBased
            ? ''
            : (action as EmbedActionConfig).availability?.[0],
        ...answerCommonProperties,
    };

    if (!isCodeBased) {
        metricsService.collectEvent(
            new UserAction(userActionTypeEnum.CUSTOM_ACTION_CLICKED),
            eventParams,
        );
    } else {
        const eventParamsForCBCA = {
            ...getEventParamsForCBCA(action as CustomAction),
            ...answerCommonProperties,
        };
        metricsService.collectEvent(
            new UserAction(userActionTypeEnum.CODE_BASED_CUSTOM_ACTION_CLICKED),
            eventParamsForCBCA,
        );
    }

    const combinedEmbedAnswerData = {
        ...cachedAnswerFragment?.answer,
        ...embedAnswerData,
    };

    let embedAction: EmbedActionConfig;

    if (isCodeBased) {
        // Create a mock EmbedActionConfig for code-based actions that will always trigger callback
        const codeBasedAction = action as CustomAction;
        embedAction = {
            id: codeBasedAction.id,
            name: codeBasedAction.name,
            type: ActionTypes.Callback, // Always use callback for code-based actions
            detail: {
                function: codeBasedAction.id, // Use the action ID as the function name
            },
            context: (action as CustomAction).position as unknown,
            availability: ['GLOBAL'],
        } as EmbedActionConfig;
    } else {
        // Use the original embed action for global actions
        embedAction = action as EmbedActionConfig;
    }

    // Only generate auth header for URL-based actions
    // Code-based actions and callback-type global actions don't need auth headers
    const authHeader =
        embedAction.type === ActionTypes.Url
            ? urlAuthHeadersForCutomActions(embedAction.detail)
            : {};

    customActionService({
        action: embedAction,
        embedAnswerData: combinedEmbedAnswerData,
        authHeader,
        dispatchEmbedEvent,
        showToastAlert,
        showBannerAlert,
        acSession,
        bachSession: getBachSession(),
        isChartMode: displayMode === DisplayMode.ChartMode,
        convMetadata,
    });
};

/**
 * Common filtering logic for visualization context (both primary, menu, and context menu positions)
 */
export const filterCodeBasedCustomActionsForVizContext = (
    embedConfig: EmbedContextOptions,
    vizId: string,
    worksheetId: string | undefined,
    columnsName: string[],
    position: CustomActionsPosition,
    liveboardId?: string,
    answerId?: string,
    legacyCustomActions: any = [],
): CustomAction[] => {
    if (!vizId || !answerId) {
        return [];
    }
    const { codeBasedCustomActions = [] } = embedConfig;
    const errors: string[] = [];

    const filteredActions = codeBasedCustomActions.filter(action => {
        // Check if action is for the specified position and VIZ target
        if (
            action.position !== position ||
            action.target !== CustomActionTarget.VIZ ||
            shouldHideForEmbed(embedConfig, action.id)
        ) {
            return false;
        }

        // Check if legacy custom actions exist at primary action position and throw error if they do
        if (
            legacyCustomActions &&
            legacyCustomActions.length > 0 &&
            position === CustomActionsPosition.PRIMARY
        ) {
            const errorMessage = CODE_BASED_CUSTOM_ACTION_ERROR_MESSAGES.LEGACY_CUSTOM_ACTIONS_CONFLICT();
            errors.push(errorMessage);
            return false;
        }

        // Use isActionAllowed for vizIds filtering
        const vizIdResult = isActionAllowed(
            action,
            FILTER_TYPES.VIZ_IDS,
            [vizId],
            {
                emptyErrorType: CodeBasedCustomActionErrorType.VizIdsEmpty,
                noMatchErrorType: CodeBasedCustomActionErrorType.InvalidVizId,
            },
            embedConfig.dispatchEmbedEvent,
            { currentVizId: vizId },
        );

        // Use isActionAllowed for liveboardIds filtering
        const liveboardIdResult = isActionAllowed(
            action,
            FILTER_TYPES.LIVEBOARD_IDS,
            [liveboardId],
            {
                emptyErrorType:
                    CodeBasedCustomActionErrorType.LiveboardIdsEmpty,
                noMatchErrorType:
                    CodeBasedCustomActionErrorType.LiveboardIdNotMatched,
            },
            embedConfig.dispatchEmbedEvent,
            { currentPinboardId: liveboardId },
        );

        // Use isActionAllowed for answerIds filtering
        const answerIdResult = isActionAllowed(
            action,
            FILTER_TYPES.ANSWER_IDS,
            [answerId],
            {
                emptyErrorType: CodeBasedCustomActionErrorType.AnswerIdsEmpty,
                noMatchErrorType:
                    CodeBasedCustomActionErrorType.InvalidAnswerId,
            },
            embedConfig.dispatchEmbedEvent,
            { currentAnswerId: answerId },
        );

        // Use isActionAllowed for dataModelIds filtering
        const modelIdResult = isActionAllowed(
            action,
            FILTER_TYPES.MODEL_IDS,
            [worksheetId],
            {
                emptyErrorType: CodeBasedCustomActionErrorType.ModelIdsEmpty,
                noMatchErrorType: CodeBasedCustomActionErrorType.InvalidModelId,
            },
            embedConfig.dispatchEmbedEvent,
            { currentModelId: worksheetId },
        );

        // Check column name condition (this is visualization-specific, so we keep it separate)
        const columnNameMatch = action?.dataModelIds?.modelColumnNames?.some(
            (columnNameWithWorksheet: string) => {
                const [
                    actionWorksheetId,
                    columnName,
                ] = columnNameWithWorksheet.split('::');
                return (
                    actionWorksheetId === worksheetId &&
                    columnsName.includes(columnName)
                );
            },
        );

        // Firstly think about global custom action for that, dataModelIds, metadataIds should not be present
        // in that case, modelIdsResult and answerId result both should be true.
        if (modelIdResult.isActionAllowed && answerIdResult.isActionAllowed) {
            return true;
        }

        if (
            modelIdResult.isValueMatch ||
            answerIdResult.isValueMatch ||
            liveboardIdResult.isValueMatch ||
            vizIdResult.isValueMatch ||
            columnNameMatch
        ) {
            return true;
        }

        // If we reach here, IDs were provided but none matched - collect errors for this action
        if (vizIdResult.error) {
            errors.push(vizIdResult.error);
        }
        if (liveboardIdResult.error) {
            errors.push(liveboardIdResult.error);
        }
        if (answerIdResult.error) {
            errors.push(answerIdResult.error);
        }
        if (modelIdResult.error) {
            errors.push(modelIdResult.error);
        }

        const columnNamesData = action?.dataModelIds?.modelColumnNames;
        if (columnNamesData && Array.isArray(columnNamesData)) {
            let error = '';
            if (columnNamesData.length)
                error = getCodeBasedCustomActionErrorMessage(
                    CodeBasedCustomActionErrorType.InvalidModelColumnNames,
                    action.name,
                    action.id,
                    {
                        currentWorksheetId: worksheetId || '',
                        availableColumns: columnsName?.join(', '),
                        modelColumnNames:
                            action?.dataModelIds?.modelColumnNames?.join(
                                ', ',
                            ) || '',
                    },
                );
            else
                error = getCodeBasedCustomActionErrorMessage(
                    CodeBasedCustomActionErrorType.ModelColumnNamesEmpty,
                    action.name,
                    action.id,
                );
            errors.push(error);
        }
        return false;
    });

    // Enforce single primary action for viz context
    if (
        position === CustomActionsPosition.PRIMARY &&
        filteredActions.length > 1
    ) {
        const first = filteredActions[0];
        const vizMultiPrimary = (CODE_BASED_CUSTOM_ACTION_ERROR_MESSAGES as any)
            .MULTIPLE_PRIMARY_ACTIONS_FOUND_VIZ;
        errors.push(
            vizMultiPrimary
                ? vizMultiPrimary(first.name, first.id)
                : `Multiple primary code-based custom actions found for Visualization. Showing only the first: "${first.name}" (ID: ${first.id}).`,
        );
        // keep only the first
        filteredActions.splice(1);
    }

    // Dispatch all collected errors at once
    if (errors.length > 0) {
        embedConfig.dispatchEmbedEvent(EventType.Error, {
            errors,
        });
        logger.error(errors);
    }

    return filteredActions;
};

/**
 * Convenience helper to fetch both PRIMARY and MENU code-based actions for Answer context
 * in one place by internally invoking the single-position filter twice.
 * Keeps the original filter behavior unchanged for callers that need only one position.
 */
export const filterCodeBasedCustomActionsForAnswerPrimaryAndMenu = (
    embedConfig: EmbedContextOptions,
    answerId: string,
    worksheetId: string | undefined,
    columnsName: string[],
    legacyPrimaryCustomActions: any[] = [],
    legacyMenuCustomActions: any[] = [],
) => {
    const primaryActions = filterCodeBasedCustomActionsForAnswerContext(
        embedConfig,
        answerId,
        worksheetId,
        columnsName,
        CustomActionsPosition.PRIMARY,
        legacyPrimaryCustomActions,
    );
    const menuActions = filterCodeBasedCustomActionsForAnswerContext(
        embedConfig,
        answerId,
        worksheetId,
        columnsName,
        CustomActionsPosition.MENU,
        legacyMenuCustomActions,
    );
    return { primaryActions, menuActions };
};

/**
 * Convenience helper to fetch both PRIMARY and MENU code-based actions for Viz context
 * in one place by internally invoking the single-position filter twice.
 * Keeps the original filter behavior unchanged for callers that need only one position.
 */
export const filterCodeBasedCustomActionsForVizPrimaryAndMenu = (
    embedConfig: EmbedContextOptions,
    vizId: string,
    worksheetId: string | undefined,
    columnsName: string[],
    liveboardId?: string,
    answerId?: string,
    legacyPrimaryCustomActions: any[] = [],
    legacyMenuCustomActions: any[] = [],
) => {
    const primaryActions = filterCodeBasedCustomActionsForVizContext(
        embedConfig,
        vizId,
        worksheetId,
        columnsName,
        CustomActionsPosition.PRIMARY,
        liveboardId,
        answerId,
        legacyPrimaryCustomActions,
    );
    const menuActions = filterCodeBasedCustomActionsForVizContext(
        embedConfig,
        vizId,
        worksheetId,
        columnsName,
        CustomActionsPosition.MENU,
        liveboardId,
        answerId,
        legacyMenuCustomActions,
    );
    return { primaryActions, menuActions };
};
